import { useRef, useState } from 'react';
import { CheckCircle2, Copy, Upload, XCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { uploadStatement } from '../../lib/bankStatementsApi';
import { ApiError } from '../../lib/api';

// Setări → Onboarding → Extrase: every statement PDF of the firm at once.
// They go up one at a time (the server parses each before answering), in
// chronological order — the period read from the file name, so an account's
// months arrive in order. Duplicates (same file, same IBAN + period) are
// refused by the server and reported here, not as failures.

type ItemState = 'waiting' | 'uploading' | 'done' | 'duplicate' | 'failed';

interface Item {
  file: File;
  state: ItemState;
  note: string | null;
}

const MONTHS: Record<string, number> = {
  ian: 1,
  ianuarie: 1,
  jan: 1,
  january: 1,
  feb: 2,
  februarie: 2,
  february: 2,
  mar: 3,
  martie: 3,
  march: 3,
  apr: 4,
  aprilie: 4,
  april: 4,
  mai: 5,
  may: 5,
  iun: 6,
  iunie: 6,
  jun: 6,
  june: 6,
  iul: 7,
  iulie: 7,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  septembrie: 9,
  september: 9,
  oct: 10,
  octombrie: 10,
  october: 10,
  noi: 11,
  nov: 11,
  noiembrie: 11,
  november: 11,
  dec: 12,
  decembrie: 12,
  december: 12,
};

/**
 * "2025-03", "2025_03", "03.2025", "03-2025", "martie 2025", "Extras_20250301"
 * → 202503; no date in the name → null (sorted after, by name).
 */
export function periodOfFileName(name: string): number | null {
  const n = name.toLowerCase();
  const ymd = /(20\d{2})[-_.]?(0[1-9]|1[0-2])(?:[-_.]?\d{2})?(?!\d)/.exec(n);
  if (ymd) return Number(ymd[1]) * 100 + Number(ymd[2]);
  const my = /(?<!\d)(0?[1-9]|1[0-2])[-_.](20\d{2})(?!\d)/.exec(n);
  if (my) return Number(my[2]) * 100 + Number(my[1]);
  const word = /([a-zăâîșț]+)[\s_-]*(20\d{2})/.exec(n);
  if (word && MONTHS[word[1]]) return Number(word[2]) * 100 + MONTHS[word[1]];
  return null;
}

export function chronological(files: File[]): File[] {
  return [...files].sort((a, b) => {
    const pa = periodOfFileName(a.name);
    const pb = periodOfFileName(b.name);
    if (pa !== null && pb !== null && pa !== pb) return pa - pb;
    if (pa === null && pb !== null) return 1;
    if (pb === null && pa !== null) return -1;
    return a.name.localeCompare(b.name, 'ro', { numeric: true });
  });
}

export function StatementsBatchUpload({
  companyId,
  onUploaded,
}: {
  companyId: string;
  onUploaded: () => void;
}) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [running, setRunning] = useState(false);

  async function run(files: File[]) {
    const queue: Item[] = chronological(files.filter((f) => /\.pdf$/i.test(f.name))).map(
      (file) => ({
        file,
        state: 'waiting',
        note: null,
      }),
    );
    setItems(queue);
    setRunning(true);
    for (let i = 0; i < queue.length; i++) {
      const update = (patch: Partial<Item>) =>
        setItems((prev) => prev.map((it, j) => (j === i ? { ...it, ...patch } : it)));
      update({ state: 'uploading' });
      try {
        const res = await uploadStatement(queue[i].file, companyId);
        update({
          state: 'done',
          note: `${res.importedCount} linii${res.autoMatched ? `, ${res.autoMatched} potrivite automat` : ''}`,
        });
      } catch (err) {
        const duplicate = err instanceof ApiError && err.code === 'DUPLICATE_STATEMENT';
        update({
          state: duplicate ? 'duplicate' : 'failed',
          note: err instanceof Error ? err.message : 'Încărcarea a eșuat.',
        });
      }
    }
    setRunning(false);
    void qc.invalidateQueries({ queryKey: ['bank-statements'] });
    onUploaded();
  }

  const count = (s: ItemState) => items.filter((i) => i.state === s).length;

  return (
    <div className="space-y-3">
      <input
        ref={input}
        type="file"
        accept=".pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length > 0) void run(files);
        }}
      />
      <button
        type="button"
        disabled={running}
        onClick={() => input.current?.click()}
        className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
      >
        <Upload className="h-4 w-4" />
        {running ? 'Se încarcă…' : 'Alege PDF-urile extraselor'}
      </button>
      {items.length > 0 && (
        <>
          <p className="text-xs text-gray-600">
            {count('done')} încărcate · {count('duplicate')} existau deja · {count('failed')} eșuate
            · {count('waiting') + count('uploading')} în așteptare
          </p>
          <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200 text-sm">
            {items.map((it) => (
              <li key={it.file.name} className="flex items-center gap-2 px-3 py-1.5">
                {it.state === 'done' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                {it.state === 'duplicate' && <Copy className="h-4 w-4 text-gray-400" />}
                {it.state === 'failed' && <XCircle className="h-4 w-4 text-red-600" />}
                {(it.state === 'waiting' || it.state === 'uploading') && (
                  <span
                    className={`h-4 w-4 rounded-full border-2 ${
                      it.state === 'uploading'
                        ? 'animate-spin border-brand-500 border-t-transparent'
                        : 'border-gray-300'
                    }`}
                  />
                )}
                <span className="truncate text-gray-900">{it.file.name}</span>
                {it.note && (
                  <span
                    className={`ml-auto shrink-0 text-xs ${it.state === 'failed' ? 'text-red-700' : 'text-gray-500'}`}
                  >
                    {it.note}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
